// src/forms/html_element_helpers/error_tracking.js

import { fetch_with_timeout } from '../../helpers';
import merge from 'lodash.merge';

import { build_data_hub_action_privacy_headers } from '../../helpers/data_hub_privacy_policy';
import { merge_user_data_browser } from '../../helpers/user_data_smart_merge';

const default_maximum_error_tracking_requests_per_page = 3;
// Fallback bucket key for callers that don't supply one (e.g. internal callers without
// an element instance). Internal-only — never sent to the backend.
const SHARED_ERROR_TRACKING_BUCKET_KEY = '__shared__';


// TODO 260426 update so it is true error track. pass the full stack trace. post dog aligned

/**
 * Per-bucket rate limit state. Previously the counter lived directly on data_hub_instance
 * (a singleton-ish window.data_hub), so two <data-hub-signup-form> elements on the same page
 * shared one quota — first form's errors blew the limit, second form's errors silently dropped.
 *
 * Now keyed by an opaque bucket key (typically the element instance's element_uuid). Two
 * elements with the same explicit data-hub-form-id still get separate quotas, and the bucket
 * key is internal — never logged or sent to the backend.
 *
 * @param {Object} data_hub_instance
 * @param {string} bucket_key
 * @param {number} maximum_error_tracking_requests_per_page
 * @returns {{count: number, in_flight: boolean, max: number}|null}
 */
const get_rate_limited_error_tracking_state = (
	data_hub_instance,
	bucket_key,
	maximum_error_tracking_requests_per_page = default_maximum_error_tracking_requests_per_page
) => {
	if (!data_hub_instance || typeof data_hub_instance !== 'object') {
		return null;
	}

	if (!(data_hub_instance.error_tracking_state_by_bucket instanceof Map)) {
		data_hub_instance.error_tracking_state_by_bucket = new Map();
	}

	const key = (typeof bucket_key === 'string' && bucket_key.length > 0)
		? bucket_key
		: SHARED_ERROR_TRACKING_BUCKET_KEY;
	let state = data_hub_instance.error_tracking_state_by_bucket.get(key);
	if (!state) {
		state = { count: 0, in_flight: false, max: maximum_error_tracking_requests_per_page };
		data_hub_instance.error_tracking_state_by_bucket.set(key, state);
	}
	return state;
};

/**
 * Tracks errors by POSTing to the action endpoint with a single data_hub_events error event.
 * 
 * @param {Object} params - Named parameters.
 * @param {string} params.internal_error_message - Internal error message for debugging.
 * @param {string} params.external_error_message - User-facing error message.
 * @param {Object} [params.data_hub_instance=window?.data_hub] - The data_hub instance.
 * @param {Object} [params.user_data={}] - Current user data.
 * @param {Object} [params.metadata={}] - Merged metadata (page + form).
 * @param {Object} [params.custom_error_data={}] - Additional error-specific data.
 * @param {Function} [params.error_function] - Error callback for tracking failures (optional, won't throw).
 * 
 * @returns {Promise<void>} Promise that resolves when error tracking is complete (or fails silently).
 */
export async function track_error_to_action_endpoint({
	internal_error_message,
	external_error_message,
	data_hub_instance = window?.data_hub,
	user_data = {},
	metadata = {},
	custom_error_data = {},
	error_function = null
}) {
	// Don't track if no data_hub_instance or forms_domain
	if (!data_hub_instance || !data_hub_instance.forms_domain) {
		if (error_function) {
			error_function?.('Error tracking skipped: no data_hub_instance or forms_domain', null);
		}
		return;
	}

	try {
		// Create a single error event for logging
		const error_events = [
			{
				verb: 'data_hub_events',
				data: {
					event_name: 'error',
					source: 'data_hub_form',
					custom_data: {
						...custom_error_data,
						internal_error_message: internal_error_message,
						external_error_message: external_error_message,
						error_timestamp: new Date().toISOString()
					}
				}
			}
		];

		let merged_user_data = await merge_user_data_browser({}, data_hub_instance?.user_data || {});
		merged_user_data = await merge_user_data_browser(merged_user_data, window?.data_hub_user_data || {});
		merged_user_data = await merge_user_data_browser(merged_user_data, user_data || {});

		// Merge metadata
		const merged_metadata = merge(
			{},
			data_hub_instance?.page_metadata || {},
			metadata
		);

		// Build the action object to send
		const error_action_object = {
			events: error_events,
			returns: {
				user_data: '{{user_data}}',
				metadata: '{{metadata}}',
				jwt_cookie: '{{jwt_cookie}}'
			},
			user_data: merged_user_data,
			metadata: merged_metadata
		};

		// Add JWT if available
		if (data_hub_instance.jwt_cookie) {
			error_action_object.jwt = data_hub_instance.jwt_cookie;
		}

		if (data_hub_instance.organization_id) {
			error_action_object.organization_id = data_hub_instance.organization_id.trim();
		}

		// Build the API URL
		const error_tracking_api_url = `${data_hub_instance.forms_domain}/api/post/user_data`;

		// Create headers
		const headers = {
			'Content-Type': 'application/json',
			...build_data_hub_action_privacy_headers(data_hub_instance),
		};

		// POST to action endpoint (fire and forget - don't block on errors)
		const response = await fetch_with_timeout(error_tracking_api_url, {
			method: 'POST',
			headers: headers,
			credentials: 'include',
			body: JSON.stringify(error_action_object),
			timeout: 20000
		}).catch(error => {
			// Silently fail - we don't want error tracking to cause additional errors
			if (error_function) {
				error_function?.('Error tracking POST failed (non-blocking): ' + error, null);
			}
			return null;
		});

		// Return parsed response (caller can read jwt_cookie for unknown_uuid correlation)
		if (response?.ok) {
			try {
				return await response.json();
			} catch (_) {
				return null;
			}
		}

	} catch (error) {
		// Silently fail - we don't want error tracking to cause additional errors
		if (error_function) {
			error_function?.('Error in track_error_to_action_endpoint (non-blocking): ' + error, null);
		}
	}
}

/**
 * Logs an error to the console and tracks it to the action endpoint with per-instance rate limiting.
 *
 * @param {Object} params
 * @param {string} params.internal_error_message
 * @param {string} params.external_error_message
 * @param {Object} [params.data_hub_instance=window?.data_hub]
 * @param {Object} [params.user_data]
 * @param {Object} [params.metadata]
 * @param {Object} [params.custom_error_data]
 * @param {number} [params.maximum_error_tracking_requests_per_page=3]
 * @returns {Promise<Object|null>}
 */
export async function log_console_error_and_track_to_action_endpoint_with_rate_limit({
	internal_error_message = null,
	external_error_message = null,
	data_hub_instance = window?.data_hub,
	user_data = data_hub_instance?.user_data || {},
	metadata = data_hub_instance?.page_metadata || {},
	custom_error_data = {},
	rate_limit_bucket_key = null,
	maximum_error_tracking_requests_per_page = default_maximum_error_tracking_requests_per_page
} = {}) {
	console.error(internal_error_message, external_error_message);

	if (!data_hub_instance || typeof data_hub_instance !== 'object' || !data_hub_instance.forms_domain) {
		return null;
	}

	const state = get_rate_limited_error_tracking_state(
		data_hub_instance,
		rate_limit_bucket_key,
		maximum_error_tracking_requests_per_page
	);

	if (!state || state.count >= state.max || state.in_flight) {
		return null;
	}

	state.count += 1;
	state.in_flight = true;

	return track_error_to_action_endpoint({
		internal_error_message,
		external_error_message,
		data_hub_instance,
		user_data,
		metadata,
		custom_error_data,
		error_function: null
	}).then((response_data) => {
		const returned_jwt = response_data?.returns?.jwt_cookie || response_data?.jwt_cookie;
		if (returned_jwt && !data_hub_instance.jwt_cookie) {
			data_hub_instance.jwt_cookie = returned_jwt;
		}
		return response_data;
	}).catch(() => {
		return null;
	}).finally(() => {
		state.in_flight = false;
	});
}

